import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  ContentCopy as CopyIcon,
  FileDownload as DownloadIcon,
  Visibility as ViewIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  DataTable,
  PageHeader,
  ErrorState,
  type Column,
  type SortDirection,
} from '../components/common';

type ApiKey = {
  id: number;
  name?: string;
  api_key: string;
  created_at?: string;
  last_used_at?: string;
  active?: boolean;
  max_tokens_monthly?: number | null;
};

export default function ApiKeys() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' | 'warning' | 'info' }>({ open: false, message: '', severity: 'success' });
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  
  // Sorting
  const [sortBy, setSortBy] = useState<string>('created_at');
  const [sortDir, setSortDir] = useState<SortDirection>('desc');
  
  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    name: '',
    max_tokens_monthly: ''
  });

  // `?for=<label>&returnTo=<path>` (e.g. from Chatbot Studio) opens the create dialog and offers a way back.
  const [searchParams, setSearchParams] = useSearchParams();
  const [returnTarget] = useState<{ label: string; path: string } | null>(() => {
    const label = (searchParams.get('for') || '').trim().slice(0, 80);
    const path = searchParams.get('returnTo') || '';
    return label && /^\/(?!\/)/.test(path) ? { label, path } : null;
  });
  const [createdForReturn, setCreatedForReturn] = useState(false);

  useEffect(() => {
    if (!searchParams.has('for') && !searchParams.has('returnTo')) return;
    if (returnTarget) {
      setCreateForm({ name: `${returnTarget.label} website`, max_tokens_monthly: '' });
      setCreateOpen(true);
    }
    setSearchParams({}, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadKeys = async () => {
    setLoading(true);
    setError(null);
    try {
      const idToken = await user?.getIdToken();
      const params = new URLSearchParams({
        page: String(page + 1),
        page_size: String(rowsPerPage),
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      
      if (search) {
        params.append('search', search);
      }
      
      const res = await fetch(`/api/v2/api-keys?${params}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });
      
      if (!res.ok) throw new Error(await res.text());
      
      const data = await res.json();
      setKeys(data.data || []);
      setTotalCount(data.total || 0);
    } catch (e: any) {
      setError(e?.message || 'Failed to load API keys');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) {
      loadKeys();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, rowsPerPage, sortBy, sortDir]);

  const handleSearch = () => {
    setPage(0);
    loadKeys();
  };

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir('asc');
    }
    setPage(0);
  };

  const handleCreate = async () => {
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch('/api/v2/api-keys/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({
          name: createForm.name || undefined,
          max_tokens_monthly: createForm.max_tokens_monthly ? parseInt(createForm.max_tokens_monthly) : null,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setCreateOpen(false);
      setCreateForm({ name: '', max_tokens_monthly: '' });
      await loadKeys();
      if (returnTarget) setCreatedForReturn(true);
      else setSnackbar({ open: true, message: 'API key created successfully', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Failed to create API key', severity: 'error' });
    }
  };

  const handleToggleActive = async (key: ApiKey, active: boolean) => {
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch(`/api/v2/api-keys/${key.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ active }),
      });
      if (!res.ok) throw new Error(await res.text());
      await loadKeys();
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Failed to update API key', severity: 'error' });
    }
  };

  const handleDelete = async (key: ApiKey) => {
    if (!window.confirm('Are you sure you want to delete this API key?')) return;
    
    try {
      const idToken = await user?.getIdToken();
      const res = await fetch(`/api/v2/api-keys/${key.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${idToken}` },
      });
      if (!res.ok) throw new Error(await res.text());
      await loadKeys();
      setSnackbar({ open: true, message: 'API key deleted successfully', severity: 'success' });
    } catch (e: any) {
      setSnackbar({ open: true, message: e?.message || 'Failed to delete API key', severity: 'error' });
    }
  };

  const downloadAsTextFile = (text: string, keyName?: string) => {
    const filename = `${(keyName || 'api-key').replace(/[^a-zA-Z0-9_-]/g, '_')}.txt`;
    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setSnackbar({ open: true, message: 'Clipboard unavailable — key downloaded as text file', severity: 'info' });
  };

  const copyToClipboard = async (text: string, keyName?: string) => {
    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(text);
        setSnackbar({ open: true, message: 'API key copied to clipboard', severity: 'success' });
        return;
      } catch {
        // Fall through to legacy method
      }
    }

    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.left = '-9999px';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      const success = document.execCommand('copy');
      document.body.removeChild(textarea);
      if (success) {
        setSnackbar({ open: true, message: 'API key copied to clipboard', severity: 'success' });
        return;
      }
    } catch {
      // Fall through to download
    }

    downloadAsTextFile(text, keyName);
  };

  const columns: Column<ApiKey>[] = [
    {
      id: 'name',
      label: 'Name',
      sortable: true,
      render: (value) => value || '-',
    },
    {
      id: 'active',
      label: 'Active',
      sortable: true,
      render: (value, row) => (
        <FormControlLabel 
          control={
            <Switch 
              checked={!!value} 
              onChange={(e) => handleToggleActive(row, e.target.checked)} 
            />
          } 
          label={value ? 'On' : 'Off'} 
        />
      ),
    },
    {
      id: 'max_tokens_monthly',
      label: 'Monthly Token Limit',
      sortable: true,
      numeric: true,
      render: (value) => value || 'Unlimited',
    },
    {
      id: 'created_at',
      label: 'Created',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleString() : '-',
    },
    {
      id: 'last_used_at',
      label: 'Last Used',
      sortable: true,
      render: (value) => value ? new Date(value).toLocaleString() : 'Never',
    },
    {
      id: 'api_key',
      label: 'Key',
      sortable: false,
      render: (value, row) => (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
            {`${value.substring(0, 8)}...`}
          </Typography>
          <Tooltip title="Copy API key">
            <IconButton size="small" onClick={() => copyToClipboard(value, row.name)}>
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download API key as file">
            <IconButton size="small" onClick={() => downloadAsTextFile(value, row.name)}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      sortable: false,
      align: 'center',
      render: (_, row) => (
        <Stack direction="row" spacing={1} justifyContent="center">
          <Tooltip title="View runs">
            <IconButton size="small" onClick={() => navigate(`/dashboard/api-keys/${row.id}/runs`)}>
              <ViewIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" onClick={() => handleDelete(row)}>
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      ),
    },
  ];

  if (error && !loading) {
    return (
      <Box sx={{ p: 3 }}>
        <PageHeader
          title="API Keys"
          subtitle="Manage your API keys for accessing the platform"
        />
        <ErrorState error={error} onRetry={loadKeys} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <PageHeader
        title="API Keys"
        subtitle="Manage your API keys for accessing the platform"
        actions={[
          { label: 'Refresh', onClick: loadKeys, icon: <RefreshIcon />, variant: 'outlined', disabled: loading },
          { label: 'New Key', onClick: () => setCreateOpen(true), icon: <AddIcon /> },
        ]}
        searchValue={search}
        onSearchChange={setSearch}
        onSearch={handleSearch}
        searchPlaceholder="Search by name"
        showSearch
      />

      {returnTarget && createdForReturn && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" variant="outlined" onClick={() => navigate(returnTarget.path)}>
              Back to {returnTarget.label}
            </Button>
          }
        >
          Key created. Go back to finish adding {returnTarget.label} to your website; the new key is picked for you.
        </Alert>
      )}

      <DataTable
        columns={columns}
        data={keys}
        loading={loading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={totalCount}
        onPageChange={(_, p) => setPage(p)}
        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        sortBy={sortBy}
        sortDirection={sortDir}
        onSort={handleSort}
        rowKey="id"
        emptyMessage="No API keys found. Create one to get started."
      />

      {/* Create Dialog */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)}>
        <DialogTitle>Create API Key</DialogTitle>
        <DialogContent sx={{ minWidth: 400, pt: 2 }}>
          <Stack spacing={2}>
            <TextField
              label="Name (optional)"
              value={createForm.name}
              onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
              fullWidth
              helperText="A friendly name to identify this key"
            />
            <TextField
              label="Monthly Token Limit (optional)"
              type="number"
              value={createForm.max_tokens_monthly}
              onChange={(e) => setCreateForm({ ...createForm, max_tokens_monthly: e.target.value })}
              fullWidth
              InputProps={{
                inputProps: { min: 0 }
              }}
              helperText={
                returnTarget
                  ? 'Recommended for a website: set a limit so a busy or abused site can’t use up your credits.'
                  : 'Leave empty for unlimited usage'
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}>Create</Button>
        </DialogActions>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          severity={snackbar.severity} 
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}